import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical } from '@tabler/icons-react';
import cx from 'clsx';
import { Text } from '@mantine/core';
import { useListState } from '@mantine/hooks';
import classes from './DndListHandle.module.css';
import { type ExitQuestion } from '@/testmeta.types';
import { IconTrash } from '@tabler/icons-react';
import { useEffect } from 'react';

interface ItemProps {
    item?: ExitQuestion;
    index: number;
    clicked?: (index: number) => void;
    selected?: boolean;
    deleteFn?: (index: number) => void;
}

function SortableItem({ item, index, clicked, selected, deleteFn }: ItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item?.key || index,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    // if(selected){
    //     style.borderColor = 'blue';
    // }

    return (
        <div
            className={cx(classes.item, { [classes.itemDragging]: isDragging, [classes.selected]: selected })}
            ref={setNodeRef}
            style={style}
            {...attributes}
            onClick={() => {
                console.log('clicked', index);
                clicked?.(index);
            }}
        >
            {index > -1 && <><div className={classes.dragHandle} {...listeners}>
                <IconGripVertical size={18} stroke={1.5} />
            </div>
                <Text className={classes.symbol}>{index + 1}</Text></>}
            {item ? <div className={classes.itemContent}>
                <Text>{item.label}</Text>
                <Text c="dimmed" size="sm">
                    Type: {item.qType}
                </Text>
            </div> : <div>
                <Text size={'1.1rem'}>No Exit Questions</Text>
                <Text c="dimmed" size={'1.2rem'}>Add one with the editor to the right</Text>
            </div>}
            {!!deleteFn && <div className={classes.deleteHandle} onClick={() => {
                console.log('deleted', index);
                deleteFn(index);
            }}>
                <IconTrash size={18} stroke={1.5} />
            </div>}
        </div>
    );
}

export function Draggal({ items, clicked, selected, deleteFn }: { items: ExitQuestion[], clicked?: (index: number) => void, selected?: number, deleteFn?: (index: number) => void }) {
    // const ii = items.map(i => ExitQuestionSchema.parse(i));
    const [state, handlers] = useListState<ExitQuestion>(items);

    useEffect(() => {
        handlers.setState(items);
    }, [items])

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = state.findIndex((i) => i.key === active.id);
        const newIndex = state.findIndex((i) => i.key === over.id);
        handlers.setState(arrayMove(state, oldIndex, newIndex));
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={state.map((i) => i.key)} strategy={verticalListSortingStrategy}>
                {state.map((item, index) => (
                    <SortableItem key={item.key} item={item} index={index} clicked={clicked} selected={selected === index} deleteFn={deleteFn} />
                ))}
                {state.length === 0 && <SortableItem index={-1} />}
            </SortableContext>
        </DndContext>
    );
}